import React, { useEffect, useMemo, useState } from 'react';
import { searchBatchSamples } from '../services/batchService';

export function SelectExistingSampleModal({ open, pipeline, onClose, onSelect }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [resultScope, setResultScope] = useState(null);
  const queryScope = JSON.stringify([pipeline, search]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !pipeline) {
      setResults([]);
      setError(null);
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    const timer = setTimeout(() => {
      searchBatchSamples({
        pipeline,
        search: search || undefined,
        batch_status: 'draft,finalized,archived',
        sample_status: 'active',
      })
        .then((data) => {
          if (!active) return;
          setResultScope(JSON.stringify([pipeline, search]));
          setResults(data || []);
          setError(null);
        })
        .catch((err) => {
          if (!active) return;
          const detail = err?.response?.data?.detail || 'Failed to load samples.';
          setError(detail);
          setResults([]);
        })
        .finally(() => { if (active) setLoading(false); });
    }, 200);

    return () => { active = false; clearTimeout(timer); };
  }, [open, pipeline, search]);

  const groupedByBatch = useMemo(() => {
    const groups = {};
    (resultScope === queryScope ? results : []).forEach((sample) => {
      (sample.memberships || []).forEach((membership) => {
        const batchId = membership.batch_id;
        if (!groups[batchId]) {
          groups[batchId] = {
            batchId,
            batchCode: membership.batch_code,
            batchStatus: membership.batch_status,
            projectCode: sample.project_code,
            projectName: sample.project_name,
            samples: [],
          };
        }
        groups[batchId].samples.push({
          ...sample,
          ...membership,
        });
      });
    });
    return Object.values(groups);
  }, [results, resultScope, queryScope]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-4xl rounded-lg bg-white p-6 shadow-xl max-h-[80vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Use existing sample</h2>
            <p className="text-sm text-slate-600">
              Open a Sample in its Batch workspace to inspect preparation and historical Runs.
            </p>
          </div>
          <button
            className="text-slate-500 hover:text-slate-700"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center">
          <input
            className="w-full rounded border border-slate-300 p-2 text-sm"
            placeholder="Search sample, batch, or project…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <span className="text-xs text-slate-500">
            Pipeline: {pipeline === 'encapsulation' ? 'Encapsulation' : 'Size'}
          </span>
        </div>

        <div className="mt-4">
          {loading && <p className="text-sm text-slate-500">Loading samples…</p>}
          {error && (
            <p className="text-sm text-rose-600">
              {error}
            </p>
          )}
          {!loading && !error && groupedByBatch.length === 0 && (
            <p className="text-sm text-slate-500">No matching samples.</p>
          )}

          <div className="mt-3 space-y-4">
            {groupedByBatch.map((group) => (
              <div
                key={group.batchId}
                className="rounded border border-slate-200 bg-slate-50 p-3 shadow-sm"
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                      {group.batchCode}
                      <StatusPill status={group.batchStatus} />
                    </p>
                    <p className="text-xs text-slate-500">
                      {group.projectCode || group.projectName || '—'}
                    </p>
                  </div>
                  <p className="text-xs text-slate-500">
                    {group.samples.length} sample{group.samples.length === 1 ? '' : 's'}
                  </p>
                </div>
                <div className="mt-3 divide-y divide-slate-200 rounded bg-white border border-slate-200">
                  {group.samples.map((sample) => {
                    return (
                      <button
                        key={sample.batch_sample_id}
                        onClick={() => {
                          onSelect?.({
                            batchId: sample.batch_id,
                            sampleId: sample.id,
                            pipeline,
                          });
                          onClose?.();
                        }}
                        className="flex w-full items-center justify-between px-3 py-2 text-left transition hover:bg-slate-50"
                      >
                        <div className="flex flex-col">
                          <span className="text-sm font-semibold text-slate-900">
                            {sample.name}
                          </span>
                          <span className="text-xs text-slate-500">
                            {sample.identity_key || '—'}
                          </span>
                        </div>
                        <span className="text-xs font-semibold text-slate-700">
                          {(pipeline === 'size' ? sample.has_size_run : sample.has_encapsulation_run) ? 'Saved runs available' : 'Open workspace'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const colors = {
    finalized: 'bg-emerald-100 text-emerald-800 border border-emerald-200',
    draft: 'bg-amber-100 text-amber-800 border border-amber-200',
    archived: 'bg-slate-100 text-slate-700 border border-slate-200',
    void: 'bg-rose-100 text-rose-800 border border-rose-200',
  };
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase ${
        colors[status] || 'bg-slate-100 text-slate-700 border border-slate-200'
      }`}
    >
      {status}
    </span>
  );
}
